import { useQuery, useReactiveVar } from '@apollo/client';
import { Query } from 'graphql/generated/graphqlTypes';
import { GET_CURRENT_TENANT } from 'graphql/queries';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useRef } from 'react';
import { tenantsServiceTokenVar } from 'state/vars';
import { BaseHookProps } from '../../hooks.types';

const useCurrentTenant = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();
  const tenantToken = useReactiveVar(tenantsServiceTokenVar);

  const {
    loading,
    data: currentTenantData,
    error,
    refetch
  } = useQuery<Pick<Query, 'getCurrentTenant'>>(GET_CURRENT_TENANT, {
    // Don't fetch until the tenant service token is set; Apollo re-runs the
    // query automatically once the token becomes available.
    skip: !tenantToken
  });

  // When the tenant service token changes mid-flow (e.g. onboarding), Apollo has
  // no signal to refetch: the query has no variables and skip stays false. Force a
  // refetch so the hook serves the tenant belonging to the current token.
  const previousToken = useRef(tenantToken);
  useEffect(() => {
    if (tenantToken && tenantToken !== previousToken.current) {
      refetch().catch((err) => console.error(err));
    }
    previousToken.current = tenantToken;
  }, [tenantToken, refetch]);

  useEffect(() => {
    if (error) {
      handleFetchError(translation.tenant_error_fetching);
      console.error(error);
    }
  }, [error, handleFetchError, translation.tenant_error_fetching]);

  return {
    currentTenant: currentTenantData?.getCurrentTenant,
    loading
  };
};

export default useCurrentTenant;
